import { ref, computed } from "vue";
import { defineStore } from "pinia";
import useMyFetch from "@stores/useMyFetch";
import { rangesIntersect, deepCopy } from "@utils/toolBox";
import handleError from "@utils/composables/handleError";
import { captureEvent } from "@/lib/analytics";

export const useCAdsetsStore = defineStore("cAdsets", () => {
  //state
  const initialCAdsets = ref([]);
  const cAdsets = ref([]);
  const cCampaignStatusFilter = ref([]);
  const cAdsetsStatusFilter = ref([]);
  const cAdsetsGenderFilter = ref([]);
  const cAdsetsAgeFilter = ref([0, 100]);
  const cAdsetsSearchQuery = ref("");

  const cAdsetOptions = computed(() =>
    cAdsets.value.map((ad) => ({ label: ad.name, value: ad.id, c_campaign_id: ad.c_campaign_id })),
  );

  // actions
  async function fetchCAdsets(mCampaignToken) {
    try {
      const { response, data } = await useMyFetch(`/m_campaigns/${mCampaignToken}/c_adsets`).json();
      if (response.value.ok) {
        cAdsets.value = data.value;
        initialCAdsets.value = deepCopy(data.value);
      }
      return cAdsets.value;
    } catch (err) {
      handleError(err);
    }
  }

  const checkGender = (cAdset) => {
    if (cAdset.t_gender.men && cAdset.t_gender.women) {
      return "all";
    } else if (cAdset.t_gender.men && !cAdset.t_gender.women) {
      return "men";
    } else {
      return "women";
    }
  };

  const checkSearch = (cAdset) => {
    const searchQuery = cAdsetsSearchQuery.value.toLowerCase();
    if (searchQuery === "" || searchQuery === undefined) return true;
    return cAdset.name.toLowerCase().includes(searchQuery);
  };

  const clearFiltersCAdsets = () => {
    cCampaignStatusFilter.value = [];
    cAdsetsStatusFilter.value = [];
    cAdsetsGenderFilter.value = [];
    cAdsetsAgeFilter.value = [0, 100];
    cAdsetsSearchQuery.value = "";
  };

  //to make the cAdset visible according to the filters applied
  const isCAdsetVisible = (cAdset) => {
    const statusCheck = cAdsetsStatusFilter.value.length === 0 || cAdsetsStatusFilter.value.includes(cAdset.status);
    const genderCheck =
      cAdsetsGenderFilter.value.length === 0 || cAdsetsGenderFilter.value.includes(checkGender(cAdset));
    const ageCheck = rangesIntersect(cAdsetsAgeFilter.value, cAdset.t_age);
    return statusCheck && genderCheck && ageCheck && checkSearch(cAdset);
  };

  //check if changes have been made to enable save button
  const hasCAdsetsChanged = computed(() => JSON.stringify(initialCAdsets.value) !== JSON.stringify(cAdsets.value));

  async function saveCAdsetsBudgets(mCampaignToken) {
    try {
      if (hasCAdsetsChanged.value === false) return Promise.resolve(true);
      const { data, response } = await useMyFetch(`/m_campaigns/${mCampaignToken}/c_adsets/save_budgets`)
        .put({ c_adsets: cAdsets.value })
        .json();
      if (response.value.ok) {
        cAdsets.value = data.value.c_adsets;
        initialCAdsets.value = deepCopy(data.value.c_adsets);

        captureEvent("adsets_budgets_saved", { m_campaign_token: mCampaignToken });
      }
    } catch (err) {
      handleError(err);
    }
  }

  return {
    // state
    initialCAdsets,
    cAdsets,
    cCampaignStatusFilter,
    cAdsetsStatusFilter,
    cAdsetsGenderFilter,
    cAdsetsAgeFilter,
    cAdsetsSearchQuery,
    // actions
    fetchCAdsets,
    clearFiltersCAdsets,
    isCAdsetVisible,
    //getters
    hasCAdsetsChanged,
    saveCAdsetsBudgets,

    cAdsetOptions,
  };
});
